import {
  Button,
  Col,
  Flex,
  Form,
  FormInstance,
  Image,
  Input,
  List,
  message,
  Popconfirm,
  Row,
  Spin,
} from 'antd';
import Icon from '@ant-design/icons';
import AddStep from '../../features/template/form/addStep';
import { SectionBlackTitle, Hint } from '../../features/template/form/style';
import CauseCard from '../causeCard';
import ImageUpload from '../imageUpload';
import RichEditor from '../richEditor';
import SelectCauseOrIssueDrawer from '../selectCauseOrIssueDrawer';
import StepCard from '../stepCard';
import SignsField from '../signsField';
import {
  DeleteButton,
  ImageContainer,
  Section,
  BottomActionBar,
} from './styles';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  fetchTemplateCauses,
  fetchTemplateRoutines,
  selectDraftTemplate,
  selectTemplateCurrentCauses,
} from '../../slices/templates';
import { useAppDispatch } from '../../store';
import {
  Step,
  Routine,
  Cause,
  ActiveStatus,
  YesOrNo,
  SuggestionStatus,
  UploadBusinessType,
  File,
} from '../../types';
import { useTranslation } from 'react-i18next';
import { createRoutine, updateRoutine } from '../../utils/httpRequest';
import { selectUserId, selectUsername } from '../../slices/profile';
import { SVGUnlink } from '../../images';
import { parseSignNames } from '../../constants/signs';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

const RoutineForm = ({
  form,
  isSuggestion,
  readOnly,
  initialRoutine,
  onSubmit,
  onCancel,
  showFullScreenSpin,
  isSuggestionReview,
  onReview,
  templateID,
  onChange,
}: {
  templateID?: string;
  form?: FormInstance;
  isSuggestion?: boolean;
  readOnly?: boolean;
  initialRoutine?: Routine;
  showFullScreenSpin?: boolean;
  onSubmit?: (routine: Routine) => Promise<void> | void;
  onCancel?: () => void;
  isSuggestionReview?: boolean;
  onReview?: (routine: Routine) => void;
  onChange?: (routine: Routine) => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [messageApi, contextHolder] = message.useMessage();

  const draftTemplate = useSelector(selectDraftTemplate);
  const availableCauses = useSelector(selectTemplateCurrentCauses);
  const userId = useSelector(selectUserId);
  const username = useSelector(selectUsername);

  const [isLinkCausesDrawerShow, setIsLinkCausesDrawerShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [autoSaveLoading, setAutoSaveLoading] = useState(false);
  const [isStepFormShow, setIsStepFormShow] = useState(false);
  const [images, setImages] = useState<File[]>([]);
  const imagesRef = useRef<File[]>([]);
  const [routine, setRoutine] = useState<Routine>();
  const [description, setDescription] = useState<string>('');
  const currentStep = useRef<number>(-1);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleAutoSave = async (routineToSave?: Routine) => {
    if (!routine?.routineID || readOnly || isSuggestion) return;

    try {
      setAutoSaveLoading(true);
      const payload = {
        ...routineToSave,
        routineID: routine?.routineID,
        templateID: templateID || draftTemplate?.templateID,
      } as Routine;
      await updateRoutine(payload);
      dispatch(
        fetchTemplateRoutines({
          templateId: templateID || draftTemplate?.templateID || '',
        })
      );
    } catch (err) {
      console.error('Auto-save failed:', err);
    } finally {
      setAutoSaveLoading(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id && routine?.routineSteps) {
      const oldIndex = routine.routineSteps.findIndex(
        (s) => s.routineStepID === active.id
      );
      const newIndex = routine.routineSteps.findIndex(
        (s) => s.routineStepID === over.id
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedSteps = arrayMove(
          routine.routineSteps,
          oldIndex,
          newIndex
        );
        // Update stepOrder for all steps
        const stepsWithOrder = reorderedSteps.map((step, index) => ({
          ...step,
          stepOrder: index,
        }));

        const updatedRoutine = {
          ...routine,
          routineSteps: stepsWithOrder,
        };
        setRoutine(updatedRoutine);
        handleAutoSave(updatedRoutine);
      }
    }
  };

  useEffect(() => {
    currentStep.current = -1;
    setIsStepFormShow(false);
    setIsLinkCausesDrawerShow(false);

    // Normalize linkedCauses and routineSteps with stepOrder
    const normalizedRoutine: Routine = {
      routineID: initialRoutine?.routineID || '',
      name: initialRoutine?.name || '',
      activeStatus: initialRoutine?.activeStatus || ActiveStatus.ACTIVE,
      isSuggestion:
        initialRoutine?.isSuggestion ||
        (isSuggestion ? YesOrNo.YES : YesOrNo.NO),
      linkedCauses: Array.isArray(initialRoutine?.linkedCauses)
        ? initialRoutine.linkedCauses
        : [],
      images: initialRoutine?.images,
      description: initialRoutine?.description,
      templateID: initialRoutine?.templateID,
      templateName: initialRoutine?.templateName,
      routineSteps: initialRoutine?.routineSteps?.map((step, index) => ({
        ...step,
        stepOrder: step.stepOrder ?? index,
      })),
      signNames: initialRoutine?.signNames,
    };

    setRoutine(normalizedRoutine);
    setDescription(initialRoutine?.description || '');
    if (!readOnly) {
      if (initialRoutine) {
        form?.setFieldsValue(normalizedRoutine);
      } else {
        form?.resetFields();
      }
    }

    const newImages = initialRoutine?.images || [];
    setImages(newImages);
    imagesRef.current = newImages;
  }, [initialRoutine]);

  useEffect(() => {
    onChange?.(routine as Routine);
  }, [routine]);

  const handleImageUploaded = (response: any) => {
    const raw = response?.data?.[0];
    const file = raw
      ? { id: raw.id, url: raw.fileUrl || raw.url, filename: raw.filename }
      : undefined;
    const newImages = file ? [file] : [];
    setImages(newImages);
    imagesRef.current = newImages;
  };

  const validateForm = async () => {
    try {
      setLoading(true);
      await form?.validateFields();
      const linkedCauses = routine?.linkedCauses || [];
      const newRoutine = {
        ...routine,
        ...form?.getFieldsValue(),
        description,
        images: imagesRef.current,
        routineSteps: routine?.routineSteps || [],
        linkedCauses,
        signNames: routine?.signNames || '',
        routineID: routine?.routineID || 0,
        activeStatus: ActiveStatus.ACTIVE,
        isSuggestion:
          routine?.isSuggestion || (isSuggestion ? YesOrNo.YES : YesOrNo.NO),
        suggestionStatus:
          routine?.suggestionStatus || SuggestionStatus.SUBMITTED,
        ...(isSuggestion &&
          !isSuggestionReview && {
            suggestionSubmitterID: userId,
            suggestionSubmitterName: username,
          }),
        ...(isSuggestionReview && {
          suggestionReviewerID: userId,
          suggestionReviewerName: username,
        }),
      };

      setRoutine(newRoutine);
      return newRoutine;
    } catch (error) {
      console.log(error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleAddStepClick = async () => {
    const result = await validateForm();
    if (result) {
      setIsStepFormShow(true);
    }
  };

  const handleAddStep = (step: Step) => {
    if (routine) {
      const routineSteps = [...(routine.routineSteps || [])];
      let updatedRoutine: Routine;
      if (currentStep.current >= 0) {
        routineSteps?.splice(currentStep.current, 1, {
          ...routineSteps[currentStep.current],
          ...step,
        });
        updatedRoutine = {
          ...routine,
          routineSteps,
        };
      } else {
        const nextSteps = [
          ...(routine?.routineSteps || []),
          {
            ...step,
            routineID: String(routine.routineID),
            routineStepID: '',
            stepOrder: routine?.routineSteps?.length || 0,
          },
        ];
        updatedRoutine = {
          ...routine,
          routineSteps: nextSteps,
        };
      }
      setRoutine(updatedRoutine);
      handleAutoSave(updatedRoutine);
    }
    currentStep.current = -1;
    setIsStepFormShow(false);
  };

  const handleRemoveStep = (index: number) => {
    routine?.routineSteps?.splice(index, 1);
    if (routine) {
      // Reindex stepOrder after removal
      const reindexedSteps = routine.routineSteps?.map((step, i) => ({
        ...step,
        stepOrder: i,
      }));
      const updatedRoutine = {
        ...routine,
        routineSteps: reindexedSteps,
      };
      setRoutine(updatedRoutine);
      handleAutoSave(updatedRoutine);
    }
  };

  const handleLinkCauses = (causeIds: string[]) => {
    setIsLinkCausesDrawerShow(false);
    const selectedCauses = availableCauses?.filter((c: Cause) =>
      causeIds.includes(String(c.causeID))
    );
    const updatedRoutine = {
      ...routine,
      ...form?.getFieldsValue(),
      description,
      linkedCauses: selectedCauses || [],
    };
    setRoutine(updatedRoutine);
    handleAutoSave(updatedRoutine);
  };

  const handleUnlinkCause = (causeId: string) => {
    const updatedRoutine = {
      ...routine,
      ...form?.getFieldsValue(),
      description,
      linkedCauses: (routine?.linkedCauses || []).filter(
        (c) => String(c.causeID) !== causeId
      ),
    };
    setRoutine(updatedRoutine);
    handleAutoSave(updatedRoutine);
  };

  const handleUnlinkAllCauses = () => {
    setRoutine({
      ...routine,
      ...form?.getFieldsValue(),
      description,
      linkedCauses: [],
    });
  };

  const handleLinkCausesClick = () => {
    setIsLinkCausesDrawerShow(true);
  };

  const handleCancel = () => {
    onCancel?.();
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);
      const result = await validateForm();

      if (result) {
        const payload = {
          ...routine,
          ...result,
          templateID: templateID || draftTemplate?.templateID,
        };

        const res = await (routine?.routineID
          ? updateRoutine(payload)
          : createRoutine(payload));
        dispatch(
          fetchTemplateRoutines({
            templateId: templateID || draftTemplate?.templateID || '',
          })
        );
        form?.resetFields();
        setImages([]);
        setRoutine(undefined);
        messageApi.open({
          type: 'success',
          content: routine?.routineID
            ? 'Routine Updated!'
            : isSuggestion
              ? 'Suggestion Submitted'
              : 'New Routine Created!',
        });
        onSubmit?.(res.data);
      }
    } catch (err: any) {
      messageApi.open({
        type: 'error',
        content:
          err?.message ||
          `${isSuggestion ? 'Suggestion' : 'Routine'} ${routine?.routineID ? 'Updated' : 'Created'} Failed!`,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (templateID) {
      dispatch(fetchTemplateCauses({ templateId: templateID }));
    }
  }, [templateID]);

  const linkedCauses = routine?.linkedCauses || [];
  const selectedCauseIds = linkedCauses.map((c) => String(c.causeID));
  const sortedSteps = [...(routine?.routineSteps || [])].sort(
    (a, b) => (a.stepOrder ?? 0) - (b.stepOrder ?? 0)
  );

  return (
    <Spin spinning={loading}>
      {contextHolder}
      <Flex vertical gap={16}>
        <Section>
          <Row gutter={[80, 16]}>
            <Col span={8}>
              {readOnly ? (
                <ImageContainer>
                  <Image
                    src={routine?.images?.[0]?.url}
                    preview={!!routine?.images?.[0]?.url}
                    fallback="/placeholder.png"
                  />
                </ImageContainer>
              ) : (
                <ImageUpload
                  fixedWidth={false}
                  businessType={UploadBusinessType.ROUTINE}
                  imageUrl={images?.[0]?.url}
                  onUploaded={handleImageUploaded}
                ></ImageUpload>
              )}
            </Col>
            <Col span={16}>
              <Form.Item
                label={
                  <span style={{ fontWeight: readOnly ? 'bold' : 'normal' }}>
                    {t('routine_name')}
                  </span>
                }
                name="name"
                rules={[
                  {
                    required: !readOnly,
                    message: t('required_routine_name'),
                  },
                ]}
              >
                {readOnly ? (
                  routine?.name
                ) : (
                  <Input type="text" placeholder={t('enter_routine_name')} />
                )}
              </Form.Item>
              <Form.Item
                label={
                  <span style={{ fontWeight: readOnly ? 'bold' : 'normal' }}>
                    {t('description')}
                  </span>
                }
              >
                <RichEditor
                  value={description}
                  onChange={setDescription}
                  placeholder={t('enter_routine_description')}
                  readOnly={readOnly}
                  height={180}
                />
              </Form.Item>
              <Form.Item
                label={
                  <span style={{ fontWeight: readOnly ? 'bold' : 'normal' }}>
                    {t('operation_and_safety_precautions')}
                    <span>({parseSignNames(routine?.signNames).length})</span>
                  </span>
                }
                name="signNames"
              >
                <span style={{ display: 'none' }}>{routine?.signNames}</span>
                <SignsField
                  value={routine?.signNames}
                  onChange={
                    readOnly
                      ? undefined
                      : (signNames) =>
                          setRoutine({
                            ...(routine || ({} as Routine)),
                            ...form?.getFieldsValue(),
                            signNames,
                          })
                  }
                  readOnly={readOnly}
                />
              </Form.Item>
            </Col>
          </Row>
        </Section>
        <Section>
          <Flex justify="space-between">
            <SectionBlackTitle>
              Steps<span>({sortedSteps.length})</span>
            </SectionBlackTitle>
            {!readOnly && (
              <Button type="primary" ghost onClick={handleAddStepClick}>
                {t('add_new_step')}
              </Button>
            )}
          </Flex>
          {sortedSteps.length > 0 && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sortedSteps.map((s) => s.routineStepID)}
                strategy={verticalListSortingStrategy}
              >
                <List>
                  {sortedSteps.map((s, index) => (
                    <List.Item key={s?.routineStepID}>
                      <StepCard
                        step={s}
                        onEdit={() => {
                          currentStep.current = index;
                          setIsStepFormShow(true);
                        }}
                        onDelete={() => handleRemoveStep(index)}
                        readOnly={readOnly}
                      />
                    </List.Item>
                  ))}
                </List>
              </SortableContext>
            </DndContext>
          )}
        </Section>
        <Section>
          {readOnly ? (
            <SectionBlackTitle>
              Linked Causes<span>({linkedCauses.length})</span>
            </SectionBlackTitle>
          ) : (
            <>
              <Hint>{t('link_cause_hint')}</Hint>
              <Flex gap={8}>
                <Button type="primary" ghost onClick={handleLinkCausesClick}>
                  Link Cause(s)
                </Button>
              </Flex>
            </>
          )}
          {linkedCauses.length > 0 && (
            <List>
              {linkedCauses.map((cause) => (
                <List.Item
                  key={cause.causeID}
                  actions={
                    !readOnly
                      ? [
                          <Popconfirm
                            key="unlink"
                            placement="topRight"
                            title={t('unlink_cause_confirmation')}
                            okText={t('yes')}
                            cancelText={t('no')}
                            onConfirm={() =>
                              handleUnlinkCause(String(cause.causeID))
                            }
                          >
                            <DeleteButton>
                              <Icon component={SVGUnlink} />
                            </DeleteButton>
                          </Popconfirm>,
                        ]
                      : undefined
                  }
                >
                  <CauseCard cause={cause} />
                </List.Item>
              ))}
            </List>
          )}
        </Section>
        <AddStep
          open={isStepFormShow}
          onClose={() => {
            setIsStepFormShow(false);
            currentStep.current = -1;
          }}
          onConfirm={handleAddStep}
          step={routine?.routineSteps?.[currentStep.current as any] as Step}
        ></AddStep>
        {!readOnly && !isSuggestionReview && (
          <BottomActionBar>
            <Flex justify="flex-end" gap={16}>
              <Button type="primary" ghost onClick={handleCancel}>
                {t('cancel')}
              </Button>
              <Button type="primary" onClick={handleConfirm}>
                {t('confirm')}
              </Button>
            </Flex>
          </BottomActionBar>
        )}
        {showFullScreenSpin && <Spin spinning={loading} fullscreen />}
        <SelectCauseOrIssueDrawer
          open={isLinkCausesDrawerShow}
          onClose={() => {
            setIsLinkCausesDrawerShow(false);
          }}
          onConfirm={handleLinkCauses}
          data={availableCauses || []}
          selectedIds={selectedCauseIds}
        ></SelectCauseOrIssueDrawer>
      </Flex>
    </Spin>
  );
};

export default RoutineForm;
